// import { graphql } from 'react-relay';
import isEqual from 'lodash/isEqual';

// import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery';
import { getClientStream } from '@confluence/ssr-data-streaming/entry-points/client';

import { Event } from './streaming';

type ResultError = {
	message: string;
	path: string[];
	locations: any;
};

type Result = {
	result: any;
	errors: ResultError[];
};

function hasRootLevelErrors(eventResult: Result): boolean {
	if (!Array.isArray(eventResult.errors)) {
		return false;
	}

	return eventResult.errors.some((error: ResultError) => error.path && error.path.length === 1);
}

export class RelayQueryResultCache {
	private queryResults: Map<string, { result: object; variables: object }[]>;

	constructor(namespace: string) {
		this.queryResults = new Map();

		const data = getClientStream(namespace);

		data.addListener((event: Event): void => {
			if (event.type === 'result') {
				try {
					if (!hasRootLevelErrors(event.result)) {
						this.addResult(event.operation, event.result.data, event.variables);
						/**
						 * Failed to cache query result Invariant Violation: graphql: Unexpected invocation at runtime.
						 * Either the Babel transform was not set up, or it failed to identify this call site.
						 * Make sure it is being used verbatim as `graphql`.
						 *
						 * This is because the the "graphql" function does not transform the query into a GraphQLTaggedNode at runtime.
						 * It is only available at build time.
						 *
						 * We would need to fix this by one of the following:
						 * 	- Using the createOperationDescriptor provided by relay-runtime
						 * (we would still need a way to get the GraphQLTaggedNode at runtime by storing a map of operation IDs to GraphQLTaggedNodes)
						 * 	- Writing to the Relay store via the interops since those would have access to the GraphQLTaggedNode. Doing this means we would
						 * need a wrapper for Relay's hooks as well.
						 */

						// add data to a global buffer for the query id to be used by useQueryInterop to read and put into the Relay store before using fetchQuery

						window.__BUFFERED_RELAY_QUERY_RESULTS__ = window.__BUFFERED_RELAY_QUERY_RESULTS__ || {};
						window.__BUFFERED_RELAY_QUERY_RESULTS__[event.queryId] = event.result.data;
						// writeQueryInterop({
						// 	query: graphql(event.operation),
						// 	data: event.result.data,
						// 	variables: event.variables,
						// });
					}
				} catch (e) {
					// eslint-disable-next-line no-console
					console.error('Failed to cache query result', e);
					// TODO: Submit errors to observability system
				}
			}
		});
	}

	getMatchingResult(query: string, variables: object): object | null {
		const results = this.queryResults.get(query);

		if (!results) {
			return null;
		}

		const matchingQueryResult = results.findIndex((result) => isEqual(result.variables, variables));

		if (matchingQueryResult === -1) {
			return null;
		}

		const matchingData = results[matchingQueryResult].result;
		results.splice(matchingQueryResult, 1);
		this.queryResults.set(query, results);
		return matchingData;
	}

	addResult(query: string, result: object, variables: object): void {
		const results = this.queryResults.get(query) || [];
		results.push({ result, variables });
		this.queryResults.set(query, results);
	}
}
